import React from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    InputLabel,
    OutlinedInput,
    Typography
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DefaultDrawer from "../../../components/default-drawer";
import { usePaginasCustomizadasState } from "../context/paginas-customizadas-controller";
import { usePaginasCustomizadasController } from "../context/paginas-customizadas-controller";
import { SecaoForm } from "./secao-form";
import { PlainTextArea } from "../../../components/plain-text-area";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';

export const PaginaCustomizadaDrawer = () => {
    const { showAddEdit, dataRequest } = usePaginasCustomizadasState();

    const {
        save,
        closeForm,
        handleOnChangeForm,
        addNewSection,
        removeSectionByIndex,
        updateSection,
        addNewItemToSection,
        removeItemFromSection,
        updateItem,
        getFileForSection,
        setFileForSection,
        getFileForItem,
        setFileForItem,
        reorderSections
    } = usePaginasCustomizadasController();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = dataRequest.sections.findIndex((_, idx) => `section-${idx}` === active.id);
            const newIndex = dataRequest.sections.findIndex((_, idx) => `section-${idx}` === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                reorderSections(oldIndex, newIndex);
            }
        }
    };

    const actions = () => {
        return (
            <React.Fragment>
                <Grid item xs={12} sx={{ mt: 3 }}>
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        sx={{
                            borderTop: '1px solid #e0e0e0',
                            pt: 2
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={save}
                            startIcon={<SaveIcon />}
                            color="primary"
                        >
                            Salvar
                        </Button>
                    </Box>
                </Grid>
            </React.Fragment >
        )
    }

    return (
        <DefaultDrawer isOpen={showAddEdit} title={dataRequest?.id !== undefined && dataRequest?.id !== null ? 'Editar Página Customizada' : 'Adicionar Página Customizada'} onClose={closeForm}
            actions={() => {
                return actions();
            }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {/* Title e Publicada */}
                        <Grid item xs={12} md={10}>
                            <InputLabel>Título *</InputLabel>
                            <OutlinedInput
                                size="small"
                                value={dataRequest?.title || ''}
                                onChange={(e) => handleOnChangeForm('title', e.target.value)}
                                fullWidth
                                error={!dataRequest?.title || dataRequest.title.length === 0}
                                placeholder="Digite o título da página..."
                            />
                        </Grid>
                        <Grid item xs={12} md={2} display="flex" alignItems="flex-end">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={dataRequest?.published || false}
                                        onChange={(e) => handleOnChangeForm('published', e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Publicada"
                            />
                        </Grid>

                        {/* Meta Description */}
                        <Grid item xs={12}>
                            <InputLabel>Meta Description</InputLabel>
                            <PlainTextArea
                                fullWidth
                                value={dataRequest?.metaDescription || ''}
                                onChange={(e) => handleOnChangeForm('metaDescription', e.target.value)}
                                rows={3}
                                placeholder="Descrição para SEO..."
                            />
                        </Grid>

                        {/* Meta Keywords */}
                        <Grid item xs={12}>
                            <InputLabel>Meta Keywords</InputLabel>
                            <OutlinedInput
                                size="small"
                                value={dataRequest?.metaKeywords || ''}
                                onChange={(e) => handleOnChangeForm('metaKeywords', e.target.value)}
                                fullWidth
                                placeholder="palavra1, palavra2, palavra3"
                            />
                        </Grid>

                        {/* Publication Date */}
                        <Grid item xs={12}>
                            <InputLabel>Data de Publicação</InputLabel>
                            <DesktopDatePicker
                                format='DD/MM/YYYY'
                                value={dataRequest?.publicationDate ? dayjs(dataRequest.publicationDate) : null}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        handleOnChangeForm('publicationDate', newValue.toDate());
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true
                                    }
                                }}
                            />
                        </Grid>

                        {/* Divider */}
                        <Grid item xs={12}>
                            <Box sx={{ borderBottom: '2px solid #ddd', my: 2 }} />
                        </Grid>

                        {/* Sections Header */}
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6" fontWeight="bold">
                                    Seções da Página
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={addNewSection}
                                >
                                    Adicionar Seção
                                </Button>
                            </Box>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                As seções definem a estrutura e o conteúdo da página. Adicione pelo menos uma seção.
                            </Typography>
                        </Grid>

                        {/* Sections List */}
                        <Grid item xs={12}>
                            {dataRequest?.sections && dataRequest.sections.length > 0 ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={dataRequest.sections.map((_, idx) => `section-${idx}`)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {dataRequest.sections.map((section, index) => (
                                            <SecaoForm
                                                key={`section-${index}`}
                                                id={`section-${index}`}
                                                section={section}
                                                sectionIndex={index}
                                                onUpdate={updateSection}
                                                onRemove={removeSectionByIndex}
                                                onAddItem={addNewItemToSection}
                                                onRemoveItem={removeItemFromSection}
                                                onUpdateItem={updateItem}
                                                getFileForSection={getFileForSection}
                                                setFileForSection={setFileForSection}
                                                getFileForItem={getFileForItem}
                                                setFileForItem={setFileForItem}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            ) : (
                                <Box
                                    sx={{
                                        p: 4,
                                        textAlign: 'center',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 2,
                                        border: '2px dashed #ddd'
                                    }}
                                >
                                    <Typography variant="body1" color="textSecondary">
                                        Nenhuma seção adicionada ainda
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                        Clique em "Adicionar Seção" para começar a criar o conteúdo da página
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </LocalizationProvider>
        </DefaultDrawer>
    );
};
